import React, { useState } from 'react';
import Draggable from 'react-draggable';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Paper,
  IconButton,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetIcon,
  CompareArrows as CompareIcon,
  Schedule as ScheduleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';

interface ComparisonData {
  current: {
    id: string;
    assessment: {
      asymmetry: number;
      borders: number;
      color: number;
      diameter: number;
      evolution: number;
      totalScore: number;
      notes?: string;
      recommendation?: string;
    };
    imageUrl?: string;
    createdAt?: string;
    annotationDetails?: {
      label: string;
      x: number;
      y: number;
      confidence?: number;
    };
  };
  historical: {
    id: string;
    assessment: {
      asymmetry: number;
      borders: number;
      color: number;
      diameter: number;
      evolution: number;
      totalScore: number;
      notes?: string;
      recommendation?: string;
    };
    imageUrl?: string;
    createdAt?: string;
    annotationDetails?: {
      label: string;
      x: number;
      y: number;
      confidence?: number;
    };
  };
}

interface ComparisonModalProps {
  open: boolean;
  onClose: () => void;
  comparisonData: ComparisonData | null;
}

const ComparisonModal: React.FC<ComparisonModalProps> = ({
  open,
  onClose,
  comparisonData
}) => {
  const theme = useTheme();
  const [currentImageZoom, setCurrentImageZoom] = useState(1);
  const [historicalImageZoom, setHistoricalImageZoom] = useState(1);
  const [currentImagePosition, setCurrentImagePosition] = useState({ x: 0, y: 0 });
  const [historicalImagePosition, setHistoricalImagePosition] = useState({ x: 0, y: 0 });

  const getRiskLevel = (score: number) => {
    if (score <= 2) return { 
      level: 'Low Risk', 
      color: theme.palette.success.main, 
      severity: 'success' as const
    };
    if (score <= 4) return { 
      level: 'Moderate Risk', 
      color: theme.palette.warning.main, 
      severity: 'warning' as const
    };
    return { 
      level: 'High Risk', 
      color: theme.palette.error.main, 
      severity: 'error' as const
    };
  };

  const handleImageZoom = (type: 'current' | 'historical', direction: 'in' | 'out' | 'reset') => {
    const setZoom = type === 'current' ? setCurrentImageZoom : setHistoricalImageZoom;
    const setPosition = type === 'current' ? setCurrentImagePosition : setHistoricalImagePosition;

    if (direction === 'reset') {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    } else if (direction === 'in') {
      setZoom(prev => Math.min(prev + 0.3, 3));
    } else {
      setZoom(prev => Math.max(prev - 0.3, 0.5));
    }
  };

  if (!comparisonData) return null;

  const { current, historical } = comparisonData;
  const currentRisk = getRiskLevel(current.assessment.totalScore);
  const historicalRisk = getRiskLevel(historical.assessment.totalScore);
  const scoreChange = current.assessment.totalScore - historical.assessment.totalScore;

  return (
    <Draggable handle=".modal-drag-handle">
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            height: 'auto',
            position: 'absolute',
            top: 50,
            m: 0
          }
        }}
      >
        <DialogTitle 
          className="modal-drag-handle"
          sx={{ 
            cursor: 'move',
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CompareIcon color="primary" />
            <Typography variant="h6">
              ABCDE Assessment Comparison
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {/* Comparison Overview */}
          <Paper elevation={1} sx={{ p: 2, mb: 3, bgcolor: alpha(theme.palette.info.main, 0.05) }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Current Assessment
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <ScheduleIcon fontSize="inherit" />
                  {current.createdAt ? new Date(current.createdAt).toLocaleDateString() : 'Unknown date'}
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={4} sx={{ textAlign: 'center' }}>
                <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <AssessmentIcon fontSize="small" />
                  Score Change
                </Typography>
                <Chip 
                  label={`${scoreChange > 0 ? '+' : ''}${scoreChange} points`}
                  color={scoreChange > 0 ? 'error' : scoreChange < 0 ? 'success' : 'default'}
                  variant="filled"
                  sx={{ fontWeight: 600 }}
                />
              </Grid>
              
              <Grid item xs={12} md={4} sx={{ textAlign: 'right' }}>
                <Typography variant="subtitle2" color="warning.main" gutterBottom>
                  Historical Assessment
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                  <ScheduleIcon fontSize="inherit" />
                  {historical.createdAt ? new Date(historical.createdAt).toLocaleDateString() : 'Unknown date'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={3}>
            {/* Current Assessment */}
            <Grid item xs={12} md={6}>
              <Card sx={{ border: `2px solid ${currentRisk.color}` }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                    Current Assessment
                  </Typography>
                  
                  {/* Current Image */}
                  {current.imageUrl && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2">Lesion Image</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleImageZoom('current', 'out')} disabled={currentImageZoom <= 0.5}>
                            <ZoomOutIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" sx={{ px: 1, py: 0.5, minWidth: 50, textAlign: 'center' }}>
                            {Math.round(currentImageZoom * 100)}%
                          </Typography>
                          <IconButton size="small" onClick={() => handleImageZoom('current', 'in')} disabled={currentImageZoom >= 3}>
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleImageZoom('current', 'reset')}>
                            <ResetIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Box
                        sx={{
                          width: '100%',
                          height: 500,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          bgcolor: alpha(theme.palette.common.black, 0.02)
                        }}
                      >
                        <img
                          src={current.imageUrl.startsWith('http') ? current.imageUrl : `${process.env.REACT_APP_API_URL}${current.imageUrl}`}
                          alt="Current assessment"
                          style={{
                            transform: `scale(${currentImageZoom}) translate(${currentImagePosition.x / currentImageZoom}px, ${currentImagePosition.y / currentImageZoom}px)`,
                            transformOrigin: 'center center',
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            transition: 'transform 0.1s ease-out'
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {/* Current Scores */}
                  <Box sx={{ mb: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Chip label={`A: ${current.assessment.asymmetry}`} size="small" color="primary" />
                      </Grid>
                      <Grid item xs={6}>
                        <Chip label={`B: ${current.assessment.borders}`} size="small" color="primary" />
                      </Grid>
                      <Grid item xs={6}>
                        <Chip label={`C: ${current.assessment.color}`} size="small" color="primary" />
                      </Grid>
                      <Grid item xs={6}>
                        <Chip label={`D: ${current.assessment.diameter}`} size="small" color="primary" />
                      </Grid>
                      <Grid item xs={12}>
                        <Chip label={`E: ${current.assessment.evolution}`} size="small" color="primary" />
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Chip 
                    label={`Total: ${current.assessment.totalScore} - ${currentRisk.level}`}
                    color={currentRisk.severity}
                    variant="filled"
                    sx={{ mb: 2, width: '100%' }}
                  />
                  
                  {/* Current Annotation Details */}
                  {current.annotationDetails && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>Label:</strong> {current.annotationDetails.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>Position:</strong> {Math.round(current.annotationDetails.x * 100)}%, {Math.round(current.annotationDetails.y * 100)}%
                      </Typography>
                      {current.annotationDetails.confidence && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          <strong>Confidence:</strong> {Math.round(current.annotationDetails.confidence * 100)}%
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* Current Notes */}
                  {current.assessment.notes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>Notes:</strong> {current.assessment.notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Historical Assessment */}
            <Grid item xs={12} md={6}>
              <Card sx={{ border: `2px solid ${historicalRisk.color}` }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ color: 'warning.main' }}>
                    Historical Assessment
                  </Typography>
                  
                  {/* Historical Image */}
                  {historical.imageUrl && (
                    <Box sx={{ mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle2">Lesion Image</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton size="small" onClick={() => handleImageZoom('historical', 'out')} disabled={historicalImageZoom <= 0.5}>
                            <ZoomOutIcon fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" sx={{ px: 1, py: 0.5, minWidth: 50, textAlign: 'center' }}>
                            {Math.round(historicalImageZoom * 100)}%
                          </Typography>
                          <IconButton size="small" onClick={() => handleImageZoom('historical', 'in')} disabled={historicalImageZoom >= 3}>
                            <ZoomInIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" onClick={() => handleImageZoom('historical', 'reset')}>
                            <ResetIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Box
                        sx={{
                          width: '100%',
                          height: 500,
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 1,
                          overflow: 'hidden',
                          position: 'relative',
                          bgcolor: alpha(theme.palette.common.black, 0.02)
                        }}
                      >
                        <img
                          src={historical.imageUrl.startsWith('http') ? historical.imageUrl : `${process.env.REACT_APP_API_URL}${historical.imageUrl}`}
                          alt="Historical assessment"
                          style={{
                            transform: `scale(${historicalImageZoom}) translate(${historicalImagePosition.x / historicalImageZoom}px, ${historicalImagePosition.y / historicalImageZoom}px)`,
                            transformOrigin: 'center center',
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            transition: 'transform 0.1s ease-out'
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                  
                  {/* Historical Scores */}
                  <Box sx={{ mb: 2 }}>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Chip label={`A: ${historical.assessment.asymmetry}`} size="small" color="warning" variant="outlined" />
                      </Grid>
                      <Grid item xs={6}>
                        <Chip label={`B: ${historical.assessment.borders}`} size="small" color="warning" variant="outlined" />
                      </Grid>
                      <Grid item xs={6}>
                        <Chip label={`C: ${historical.assessment.color}`} size="small" color="warning" variant="outlined" />
                      </Grid>
                      <Grid item xs={6}>
                        <Chip label={`D: ${historical.assessment.diameter}`} size="small" color="warning" variant="outlined" />
                      </Grid>
                      <Grid item xs={12}>
                        <Chip label={`E: ${historical.assessment.evolution}`} size="small" color="warning" variant="outlined" />
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Chip 
                    label={`Total: ${historical.assessment.totalScore} - ${historicalRisk.level}`}
                    color={historicalRisk.severity}
                    variant="filled"
                    sx={{ mb: 2, width: '100%' }}
                  />
                  
                  {/* Historical Annotation Details */}
                  {historical.annotationDetails && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>Label:</strong> {historical.annotationDetails.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>Position:</strong> {Math.round(historical.annotationDetails.x * 100)}%, {Math.round(historical.annotationDetails.y * 100)}%
                      </Typography>
                      {historical.annotationDetails.confidence && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          <strong>Confidence:</strong> {Math.round(historical.annotationDetails.confidence * 100)}%
                        </Typography>
                      )}
                    </Box>
                  )}
                  
                  {/* Historical Notes */}
                  {historical.assessment.notes && (
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <strong>Notes:</strong> {historical.assessment.notes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Detailed Score Comparison */}
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CompareIcon color="primary" />
                  Detailed Score Comparison
                </Typography>
                
                <Grid container spacing={2}>
                  {[
                    { key: 'asymmetry', label: 'Asymmetry' },
                    { key: 'borders', label: 'Borders' },
                    { key: 'color', label: 'Color' },
                    { key: 'diameter', label: 'Diameter' },
                    { key: 'evolution', label: 'Evolution' }
                  ].map(({ key, label }) => {
                    const currentValue = current.assessment[key as keyof typeof current.assessment] as number;
                    const historicalValue = historical.assessment[key as keyof typeof historical.assessment] as number;
                    const change = currentValue - historicalValue;
                    
                    return (
                      <Grid key={key} item xs={12} sm={6} md={2.4}>
                        <Paper elevation={0} sx={{ p: 2, textAlign: 'center', border: `1px solid ${theme.palette.divider}` }}>
                          <Typography variant="subtitle2" gutterBottom>
                            {label}
                          </Typography>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="body2" color="primary.main">
                              {currentValue}
                            </Typography>
                            <Typography variant="body2" color="warning.main">
                              {historicalValue}
                            </Typography>
                          </Box>
                          <Chip
                            label={`${change > 0 ? '+' : ''}${change}`}
                            size="small"
                            color={change > 0 ? 'error' : change < 0 ? 'success' : 'default'}
                            variant="outlined"
                          />
                        </Paper>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} variant="outlined">
            Close Comparison
          </Button>
        </DialogActions>
      </Dialog>
    </Draggable>
  );
};

export default ComparisonModal;
